import { FormGroup } from 'reactstrap';
import BaseFormattedNumber, {
  NumberFormatProps,
  NumberFormatValues,
  SourceInfo,
} from 'react-number-format';
import styled from 'styled-components';
import LoadingSpinner from '@paradigm/design-system/src/components/LoadingSpinner';
import { pickledBluewood } from '@paradigm/design-system/src/colors';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { useUnifiedMarketsV2PricingAlgorithmFeatureFlag } from '@paradigm/features/src/feature-flags/context';

export interface TwoWayInputProps {
  readonly leftValue?: string;
  readonly rightValue?: string;
  readonly onLeftValueChange?: (
    values: NumberFormatValues,
    sourceInfo: SourceInfo,
  ) => void;
  readonly onRightValueChange?: (
    values: NumberFormatValues,
    sourceInfo: SourceInfo,
  ) => void;
  readonly isLeftDisabled?: boolean;
  readonly isRightDisabled?: boolean;
  readonly decimalScale?: number;
  readonly allowNegative?: boolean;
  readonly isLoading?: boolean;
  readonly className?: string;
  readonly leftHelper?: React.ReactNode;
  readonly rightHelper?: React.ReactNode;
}

export default function TwoWayInput({
  leftValue,
  rightValue,
  onLeftValueChange,
  onRightValueChange,
  decimalScale,
  allowNegative = true,
  isLeftDisabled = false,
  isRightDisabled = false,
  isLoading = false,
  className,
  leftHelper,
  rightHelper,
}: TwoWayInputProps) {
  const isV2PricingAlgoEnabled =
    useUnifiedMarketsV2PricingAlgorithmFeatureFlag();

  const isDisabled =
    isV2PricingAlgoEnabled && isRightDisabled && isLeftDisabled;

  return (
    <Container isDisabled={isDisabled} className={className}>
      <InputContainer>
        <FormattedNumber
          data-testid="two-way-bid-price"
          onValueChange={onLeftValueChange}
          value={leftValue}
          decimalScale={decimalScale}
          allowNegative={allowNegative}
          disabled={isLeftDisabled || isLoading}
        />
        {leftHelper}
      </InputContainer>
      {isLoading ? (
        <LoadingSpinner size={18} />
      ) : (
        <span css="margin: 0 0.1rem;">/</span>
      )}
      <InputContainer>
        <FormattedNumber
          data-testid="two-way-ask-price"
          onValueChange={onRightValueChange}
          value={rightValue}
          decimalScale={decimalScale}
          allowNegative={allowNegative}
          disabled={isRightDisabled || isLoading}
        />
        {rightHelper}
      </InputContainer>
    </Container>
  );
}

const FormattedNumber = (props: NumberFormatProps) => (
  <BaseFormattedNumber
    thousandSeparator
    css="flex: 1; width: 100%;"
    {...props}
  />
);

const InputContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  height: 100%;
  position: relative;
`;

const Container = styled(FormGroup).withConfig({
  shouldForwardProp: (prop) => !['isDisabled'].includes(prop.toString()),
})<{ isDisabled: boolean }>`
  display: flex;
  align-items: center;
  border-radius: 3px;
  height: 32px;
  background: ${pickledBluewood};
  margin-bottom: 10px;
  input {
    color: white;
    border: none;
    background: transparent;
    padding: 0px 10px;
    &:focus {
      outline: none;
    }
    cursor: ${(props) => (props.isDisabled ? 'not-allowed' : 'auto')};
  }
  opacity: ${(props) => (props.isDisabled ? 0.5 : 1)};

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1rem;

    input:first-child {
      text-align: right;
    }
  }
`;
